import React, { useContext, useState } from "react"
import { NavLink } from "react-router-dom"
import moment from "moment"

import { funEU } from "../../../utils/firebase"
import { ModalContext } from "../../../utils/providers/modal"
import Badge from "../../../components/ui/badge/badge"
import Button from "../../../components/ui/button/button"

/**
 * Single `<tr>` rendered inside the monitored-mailboxes `Table`.
 *
 * Action availability matrix:
 *   ACTIVE  -> Pause | Remove
 *   PAUSED  -> Resume | Remove
 *   ERROR   -> Resume (best-effort retry) | Remove
 *
 * All three actions call into the corresponding callable in the
 * `functions-communications` codebase. The parent's onSnapshot listener picks
 * up state changes automatically; no need to refresh manually.
 */
function MailboxRow({ mailbox }) {
	const { showModal } = useContext(ModalContext)
	const [busy, setBusy] = useState(null)

	const callableNames = {
		pause: "pauseMonitoredMailbox",
		resume: "resumeMonitoredMailbox",
		remove: "removeMonitoredMailbox",
	}

	const callAction = async (key) => {
		setBusy(key)
		try {
			const callable = funEU.httpsCallable(callableNames[key])
			await callable({ mailbox_id: mailbox.id })
		} catch (err) {
			showModal({
				type: "ALERT",
				title: "Action failed",
				body: err?.message || `Failed to ${key} mailbox`,
				cancel: { label: "Dismiss", action: () => null },
				next: { label: "Dismiss", action: () => null },
			})
			setBusy(null)
		}
		// On success the snapshot listener removes/updates the row, so leaving
		// `busy` set is fine — the row either re-renders with new state or
		// disappears entirely.
	}

	const confirmRemove = () => {
		showModal({
			type: "ALERT",
			title: "Remove mailbox?",
			body:
				`This stops monitoring ${mailbox.email} and tears down the Microsoft Graph subscription. ` +
				`Communications already ingested under this mailbox are kept — they remain attached to ` +
				`whichever clients/enquiries they were matched to.`,
			cancel: { label: "Cancel", action: () => null },
			next: { label: "Yes, remove", action: () => callAction("remove") },
		})
	}

	const lastReceived = mailbox.last_message_at?.seconds
		? moment.unix(mailbox.last_message_at.seconds).fromNow()
		: null

	return (
		<tr data-system-id={mailbox.id}>
			<td>{mailbox.display_name || <small className="no-value">No name set</small>}</td>

			<td>{mailbox.email}</td>

			<td>
				<StatusBadge status={mailbox.status} />
				{mailbox.status === "ERROR" && mailbox.error && (
					<small className="mailbox-row-error">{mailbox.error}</small>
				)}
			</td>

			<td>{lastReceived || <small className="no-value">—</small>}</td>

			{/*
				Action cell mirrors the bookings-received pattern: NavLink + Buttons
				sit directly inside `<td className="is-button">` with no wrapper
				div, so the table.scss `.is-button > a` styling does its primary-
				blue work and the SCSS in mailboxes.scss flips Buttons to
				`display: inline-flex` so they flow inline alongside the NavLink.
			*/}
			<td className="is-button">
				<NavLink to={`/communications/mailbox/${mailbox.id}`}>View emails</NavLink>

				{mailbox.status === "ACTIVE" && (
					<Button
						small={true}
						label="Pause"
						loading={busy === "pause"}
						loadingText="Pausing..."
						onClick={() => callAction("pause")}
					/>
				)}

				{(mailbox.status === "PAUSED" || mailbox.status === "ERROR") && (
					<Button
						small={true}
						label="Resume"
						loading={busy === "resume"}
						loadingText="Resuming..."
						onClick={() => callAction("resume")}
					/>
				)}

				<Button
					small={true}
					label="Remove"
					loading={busy === "remove"}
					loadingText="Removing..."
					onClick={() => confirmRemove()}
				/>
			</td>
		</tr>
	)
}

function StatusBadge({ status }) {
	if (status === "ACTIVE") return <Badge type="POSITIVE" label="Active" />
	if (status === "PAUSED") return <Badge type="INFO" label="Paused" />
	if (status === "ERROR") return <Badge type="NEGATIVE" label="Error" />
	return <Badge label={status || "Unknown"} />
}

export default MailboxRow
